import { useEffect, useMemo } from "react";
import { useHistory } from "react-router";

import { trackEvent } from "@hopper-b2b/api";
import {
  getChildState,
  useCheckoutSend,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";

import { MODAL_ALERT, ModalCategoryType } from "@hopper-b2b/types";
import { ErrorScreen } from "../../../components/ErrorScreen";
import { closeNubankWebview } from "../../../utils/nubankHandoff";
import { PaymentEvent } from "../../states";
import { getIsLoading, getPaymentError } from "../../states/payments/selectors";
import { LoadingScreen } from "../LoadingScreen";
import { AuthConsentScreen } from "./components/AuthConsent";
import { CreditInstallmentSelect } from "./components/CreditInstallmentSelect";
import { CreditReviewScreen } from "./components/CreditReviewScreen";
import { InvalidPaymentScreen } from "./components/InvalidPaymentScreen";
import { PaymentMethodSelect } from "./components/PaymentMethodSelect";

export const NubankPaymentComponent = () => {
  const [state] = useCheckoutState();
  const isLoading = getIsLoading(state.value);
  return isLoading ? <LoadingScreen /> : <MappedComponents />;
};

const MappedComponents = () => {
  const [state] = useCheckoutState();
  const childState = getChildState(state.value);

  switch (childState) {
    case "requestAuth":
      return <AuthConsentScreen />;
    case "selectCreditOrDebit":
      return <PaymentMethodSelect />;
    case "credit":
      return <CreditInstallmentSelect />;
    case "review":
      return <CreditReviewScreen />;
    case "invalidFunds":
      return <InvalidPaymentScreen />;
    case "error":
      return <GenericErrorScreen />;
    default:
      return null;
  }
};

enum GetPaymentMethodErrors {
  NubankGetPaymentMethodsError = "NubankGetPaymentMethodsError",
  NubankNotEnoughBalance = "NubankNotEnoughBalance",
}

const GenericErrorScreen = () => {
  const { t } = useI18nContext();
  const send = useCheckoutSend();
  const history = useHistory();
  const error = useSelector(getPaymentError);

  const errorCode = error?.details?.[0]?.code as GetPaymentMethodErrors;

  const mapErrorCodeToErrorProps = useMemo(() => {
    if (errorCode === GetPaymentMethodErrors.NubankGetPaymentMethodsError) {
      return {
        title: t("nupay.genericErrorSubheader"),
        subtitle: t("nupay.genericErrorSubheader"),
        primaryButtonText: t("back"),
        primaryOnClick: () => history.goBack(),
        onBack: () => history.goBack(),
      };
    } else if (errorCode === GetPaymentMethodErrors.NubankNotEnoughBalance) {
      return {
        title: t("nupay.noLimitErrorHeader"),
        subtitle: t("nupay.noLimitErrorSubheader"),
        primaryButtonText: t("nupay.invalidPaymentButton"),
        primaryOnClick: () => closeNubankWebview(),
        onBack: () => closeNubankWebview(),
      };
    } else {
      // Default error copy
      return {
        title: t("nupay.genericErrorSubheader"),
        subtitle: t("nupay.genericErrorSubheader"),
        primaryButtonText: t("tryAgain"),
        primaryOnClick: () => send(PaymentEvent.TRY_AGAIN),
        onBack: () => history.goBack(),
      };
    }
  }, [errorCode, history, send, t]);

  useEffect(() => {
    trackEvent({
      eventName: MODAL_ALERT,
      properties: {
        step: "payment",
        modal_code: errorCode,
        category: ModalCategoryType.TROUBLE,
        primary_button: mapErrorCodeToErrorProps.primaryButtonText,
      },
    });
  }, [errorCode, mapErrorCodeToErrorProps.primaryButtonText]);

  return <ErrorScreen className="warn" {...mapErrorCodeToErrorProps} />;
};
